import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import HotelIcon from '@mui/icons-material/Hotel';
import PercentIcon from '@mui/icons-material/Percent';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import ScienceIcon from '@mui/icons-material/Science';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';

const COPY = {
  ar: {
    title: 'إدارة الأسعار',
    subtitle: 'تحكم في أسعار الإيواء وضريبة القيمة المضافة المستخدمة في التقارير المالية ومراجعة المحاسب.',
    refresh: 'تحديث',
    add: 'إضافة نوع إيواء',
    edit: 'تعديل السعر',
    save: 'حفظ',
    cancel: 'إلغاء',
    typeNameAr: 'اسم النوع عربي',
    typeNameEn: 'اسم النوع إنجليزي',
    monthlyPrice: 'السعر الشهري',
    vat: 'الضريبة %',
    totalWithVat: 'الإجمالي مع الضريبة',
    accommodationTypes: 'أنواع الإيواء',
    reproductionProcedures: 'إجراءات عيادة الإنتاج',
    procedureName: 'اسم الإجراء',
    procedurePrice: 'سعر الإجراء',
    description: 'الوصف',
    averagePrice: 'متوسط السعر',
    highestPrice: 'أعلى سعر',
    records: 'سجل',
    noData: 'لا توجد أنواع إيواء مسجلة.',
    saved: 'تم حفظ السعر.',
    loadError: 'تعذر تحميل الأسعار.',
    saveError: 'تعذر حفظ السعر.',
    impact: 'أي تعديل هنا يؤثر على احتساب الإيواء وأسعار إجراءات الإنتاج التي تظهر للمحاسب فقط، ولا تظهر في صفحة تشغيل عيادة الإنتاج.',
  },
  en: {
    title: 'Pricing control',
    subtitle: 'Manage accommodation prices and VAT used by finance reports and accountant review.',
    refresh: 'Refresh',
    add: 'Add accommodation type',
    edit: 'Edit price',
    save: 'Save',
    cancel: 'Cancel',
    typeNameAr: 'Arabic type name',
    typeNameEn: 'English type name',
    monthlyPrice: 'Monthly price',
    vat: 'VAT %',
    totalWithVat: 'Total with VAT',
    accommodationTypes: 'Accommodation types',
    reproductionProcedures: 'Reproduction clinic procedures',
    procedureName: 'Procedure name',
    procedurePrice: 'Procedure price',
    description: 'Description',
    averagePrice: 'Average price',
    highestPrice: 'Highest price',
    records: 'records',
    noData: 'No accommodation types found.',
    saved: 'Price was saved.',
    loadError: 'Failed to load prices.',
    saveError: 'Failed to save price.',
    impact: 'Changes here affect accommodation calculations and reproduction procedure prices shown to accounting only, not the clinic operation page.',
  },
  pt: {
    title: 'Controle de preços',
    subtitle: 'Gerencie preços de acomodação e IVA usados nos relatórios financeiros e revisão contábil.',
    refresh: 'Atualizar',
    add: 'Adicionar tipo de acomodação',
    edit: 'Editar preço',
    save: 'Salvar',
    cancel: 'Cancelar',
    typeNameAr: 'Nome árabe do tipo',
    typeNameEn: 'Nome inglês do tipo',
    monthlyPrice: 'Preço mensal',
    vat: 'IVA %',
    totalWithVat: 'Total com IVA',
    accommodationTypes: 'Tipos de acomodação',
    reproductionProcedures: 'Procedimentos da clínica de reprodução',
    procedureName: 'Nome do procedimento',
    procedurePrice: 'Preço do procedimento',
    description: 'Descrição',
    averagePrice: 'Preço médio',
    highestPrice: 'Maior preço',
    records: 'registros',
    noData: 'Nenhum tipo de acomodação encontrado.',
    saved: 'Preço salvo.',
    loadError: 'Falha ao carregar preços.',
    saveError: 'Falha ao salvar preço.',
    impact: 'Alterações aqui afetam acomodação e preços de procedimentos de reprodução exibidos apenas para contabilidade.',
  },
};

const emptyForm = {
  type_id: null,
  type_name_ar: '',
  type_name_en: '',
  monthly_price: '',
  vat_percentage: '',
};

function currentLocale(language) {
  if (String(language || '').startsWith('ar')) return 'ar';
  if (String(language || '').startsWith('pt')) return 'pt';
  return 'en';
}

function formatMoney(value) {
  const number = Number(value || 0);
  return Number.isFinite(number)
    ? `${number.toLocaleString('en-US', { maximumFractionDigits: 2 })} SAR`
    : '0 SAR';
}

export default function ServicePricing() {
  const { i18n } = useTranslation();
  const text = COPY[currentLocale(i18n.language)];
  const [types, setTypes] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [tab, setTab] = useState('accommodation');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const summary = useMemo(() => {
    const prices = types.map((type) => Number(type.monthly_price || 0)).filter(Number.isFinite);
    const total = prices.reduce((sum, price) => sum + price, 0);
    return {
      count: types.length,
      average: prices.length ? total / prices.length : 0,
      highest: prices.length ? Math.max(...prices) : 0,
    };
  }, [types]);

  const loadTypes = async () => {
    setLoading(true);
    setError('');
    try {
      const [{ data }, procedureRes] = await Promise.all([
        api.get('/accommodations/types'),
        api.get('/reproduction-clinic/procedures'),
      ]);
      setTypes(Array.isArray(data) ? data : []);
      setProcedures(Array.isArray(procedureRes.data) ? procedureRes.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || text.loadError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDialog = (type = null) => {
    setMessage('');
    setError('');
    setForm(
      type
        ? {
            type_id: type.type_id,
            procedure_id: type.procedure_id || null,
            type_name_ar: type.type_name_ar || '',
            type_name_en: type.type_name_en || type.procedure_name || '',
            monthly_price: type.monthly_price ?? type.default_price ?? '',
            vat_percentage: type.vat_percentage ?? '',
            description: type.description || '',
          }
        : emptyForm
    );
    setDialogOpen(true);
  };

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const saveType = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        type_name_ar: form.type_name_ar,
        type_name_en: form.type_name_en,
        monthly_price: Number(form.monthly_price || 0),
        vat_percentage: Number(form.vat_percentage || 0),
      };

      if (form.procedure_id) {
        await api.put(`/reproduction-clinic/procedures/${form.procedure_id}`, {
          procedure_name: form.type_name_en,
          default_price: Number(form.monthly_price || 0),
          description: form.description || '',
          user_id: localStorage.getItem('userId') || null,
        });
      } else if (form.type_id) {
        await api.put(`/accommodations/types/${form.type_id}`, payload);
      } else {
        await api.post('/accommodations/types', payload);
      }

      setDialogOpen(false);
      setMessage(text.saved);
      await loadTypes();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || text.saveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      className="smart-page-container"
      sx={{ minHeight: '100vh', bgcolor: COLORS.page, maxWidth: 'var(--app-page-max)', mx: 'auto' }}
    >
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Box>
            <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
              {text.title}
            </Typography>
            <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>{text.subtitle}</Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button startIcon={<RefreshIcon />} onClick={loadTypes} disabled={loading} sx={{ color: COLORS.gold, fontWeight: 900 }}>
              {text.refresh}
            </Button>
            {tab === 'accommodation' && (
              <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => openDialog()}
              sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}
              >
                {text.add}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}
      <Alert severity="info" sx={{ mb: 2, bgcolor: COLORS.blueSoft, color: COLORS.text }}>
        {text.impact}
      </Alert>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Metric icon={<HotelIcon />} label={text.accommodationTypes} value={`${summary.count} ${text.records}`} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Metric icon={<PriceChangeIcon />} label={text.averagePrice} value={formatMoney(summary.average)} color={COLORS.green} soft={COLORS.greenSoft} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Metric icon={<PercentIcon />} label={text.highestPrice} value={formatMoney(summary.highest)} color={COLORS.blue} soft={COLORS.blueSoft} />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ px: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <Tab value="accommodation" icon={<HotelIcon />} iconPosition="start" label={text.accommodationTypes} sx={{ fontWeight: 900 }} />
          <Tab value="reproduction" icon={<ScienceIcon />} iconPosition="start" label={text.reproductionProcedures} sx={{ fontWeight: 900 }} />
        </Tabs>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
            {tab === 'accommodation' ? text.accommodationTypes : text.reproductionProcedures}
          </Typography>
          <Chip label={`${tab === 'accommodation' ? types.length : procedures.length}`} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 900 }} />
        </Stack>

        {tab === 'accommodation' ? (
          <TableContainer sx={smartTableContainerSx}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell>{text.typeNameAr}</TableCell>
                <TableCell>{text.typeNameEn}</TableCell>
                <TableCell>{text.monthlyPrice}</TableCell>
                <TableCell>{text.vat}</TableCell>
                <TableCell>{text.totalWithVat}</TableCell>
                <TableCell align="center">{text.edit}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {types.map((type) => (
                <TableRow key={type.type_id} hover>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 900 }}>{type.type_name_ar}</TableCell>
                  <TableCell sx={{ color: COLORS.text }}>{type.type_name_en}</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>{formatMoney(type.monthly_price)}</TableCell>
                  <TableCell>{Number(type.vat_percentage || 0).toLocaleString('en-US')}%</TableCell>
                  <TableCell sx={{ fontWeight: 900, color: COLORS.green }}>{formatMoney(type.total_price_with_vat)}</TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => openDialog(type)}
                      sx={{ color: COLORS.gold, fontWeight: 900 }}
                    >
                      {text.edit}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!types.length && !loading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>{text.noData}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </TableContainer>
        ) : (
          <TableContainer sx={smartTableContainerSx}>
            <Table size="small" sx={smartTableSx}>
              <TableHead sx={smartTableHeadSx}>
                <TableRow>
                  <TableCell>{text.procedureName}</TableCell>
                  <TableCell>{text.description}</TableCell>
                  <TableCell>{text.procedurePrice}</TableCell>
                  <TableCell align="center">{text.edit}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {procedures.map((procedure) => (
                  <TableRow key={procedure.procedure_id} hover>
                    <TableCell sx={{ color: COLORS.text, fontWeight: 900 }}>{procedure.procedure_name}</TableCell>
                    <TableCell sx={{ color: COLORS.muted }}>{procedure.description || '-'}</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>{formatMoney(procedure.default_price)}</TableCell>
                    <TableCell align="center">
                      <Button size="small" startIcon={<EditIcon />} onClick={() => openDialog(procedure)} sx={{ color: COLORS.gold, fontWeight: 900 }}>
                        {text.edit}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!procedures.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>{text.noData}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{form.procedure_id || form.type_id ? text.edit : text.add}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!form.procedure_id && <TextField
              label={text.typeNameAr}
              value={form.type_name_ar}
              onChange={(event) => updateField('type_name_ar', event.target.value)}
              fullWidth
            />}
            <TextField
              label={form.procedure_id ? text.procedureName : text.typeNameEn}
              value={form.type_name_en}
              onChange={(event) => updateField('type_name_en', event.target.value)}
              fullWidth
            />
            <TextField
              label={form.procedure_id ? text.procedurePrice : text.monthlyPrice}
              type="number"
              value={form.monthly_price}
              onChange={(event) => updateField('monthly_price', event.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              fullWidth
            />
            {!form.procedure_id && <TextField
              label={text.vat}
              type="number"
              value={form.vat_percentage}
              onChange={(event) => updateField('vat_percentage', event.target.value)}
              inputProps={{ min: 0, max: 100, step: '0.01' }}
              fullWidth
            />}
            {form.procedure_id && (
              <TextField
                label={text.description}
                value={form.description || ''}
                onChange={(event) => updateField('description', event.target.value)}
                fullWidth
                multiline
                rows={3}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: COLORS.muted, fontWeight: 900 }}>
            {text.cancel}
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={saveType}
            variant="contained"
            disabled={saving}
            sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}
          >
            {text.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Metric({ icon, label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 104 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: '50%',
            bgcolor: soft,
            color,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900, overflowWrap: 'anywhere' }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
